import { useEffect, useRef, useState } from 'react';
import { X, Loader2, AlertCircle, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useApiToken } from '@/auth/hooks';
import { DocumentBlobPreview } from '@/components/document/DocumentBlobPreview';

interface DocumentViewerDialogProps {
  documentId: string | null;
  /** When set to 'interview_report' or 'interview_source', fetches from Neo4j via Python API */
  sourceType?: string;
  fileName?: string;
  onClose: () => void;
}

export function DocumentViewerDialog({ documentId, sourceType, fileName, onClose }: DocumentViewerDialogProps) {
  const { getAccessToken } = useApiToken();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [mimeType, setMimeType] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevUrlRef = useRef<string | null>(null);

  // Choose API endpoint based on source type
  const isInterviewReport = sourceType === 'interview_report';
  const isInterviewSource = sourceType === 'interview_source';

  // Fetch the document when dialog opens
  useEffect(() => {
    if (!documentId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    setBlob(null);

    (async () => {
      try {
        // Attempt token acquisition; fall back gracefully on timeout / MSAL errors
        let token: string | null = null;
        try {
          token = await getAccessToken();
        } catch {
          // MSAL silent timeout or other auth error – proceed without Bearer token.
          // The backend may still serve the document (e.g. cookie-based session).
        }
        const fetchUrl = isInterviewReport
          ? `/api/python/interview/reports/${documentId}/pdf`
          : isInterviewSource
            ? `/api/python/interview/documents/${documentId}/view`
            : `/api/dotnet/documents/${documentId}/view`;
        const res = await fetch(fetchUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const blob = await res.blob();
        if (cancelled) return;

        const url = URL.createObjectURL(blob);
        setBlob(blob);
        setMimeType(blob.type || 'application/octet-stream');
        setBlobUrl(url);
        prevUrlRef.current = url;
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Dokument konnte nicht geladen werden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [documentId, isInterviewReport, isInterviewSource, getAccessToken]);

  // Revoke blob URL on close / documentId change
  useEffect(() => {
    return () => {
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = null;
      }
    };
  }, [documentId]);

  const title = fileName ?? (documentId ? `Dokument ${documentId.slice(0, 8)}…` : 'Dokument');

  return (
    <Dialog open={!!documentId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        showCloseButton={false}
        className="flex flex-col gap-0 p-0 overflow-hidden"
        style={{ width: '80vw', maxWidth: '1100px', height: '88vh' }}
        aria-describedby={undefined}
      >
        {/* Header */}
        <DialogHeader className="shrink-0 flex flex-row items-center gap-3 px-5 py-3 border-b border-border">
          <DialogTitle className="flex-1 truncate text-sm font-semibold">{title}</DialogTitle>
          {blobUrl && (
            <a
              href={blobUrl}
              download={fileName ?? `document-${documentId?.slice(0, 8)}`}
              className="shrink-0"
            >
              <Button variant="ghost" size="icon" className="size-7" title="Herunterladen" aria-label="Herunterladen">
                <Download className="size-4" />
              </Button>
            </a>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 size-7"
            onClick={onClose}
            aria-label="Dialog schließen"
          >
            <X className="size-4" />
          </Button>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 min-h-0 relative bg-muted/20">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="size-8 animate-spin" />
              <span className="text-sm">Dokument wird geladen…</span>
            </div>
          )}

          {error && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <AlertCircle className="size-8 text-destructive" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {blobUrl && (
            <DocumentBlobPreview
              blobUrl={blobUrl}
              blob={blob}
              mimeType={mimeType}
              title={title}
              fileName={fileName}
              pdfFragment="#toolbar=1&view=FitH"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
